import { useMutation } from "@tanstack/react-query";
import { editCompany } from "api/company";
import { getCurrentSite } from "api/pathfinder";
import { compiledClientConfig } from "config";
import { useCompanyContext } from "context/companyContext";
import { useEffect } from "react";
import { devLog } from "utils/devtools/devLogging";

import { useAuthenticatedUser } from "./useAuthenticatedUser";

export const useCaptureSiteName = () => {
  const { user } = useAuthenticatedUser();
  const {
    actions: { setCompany },
    state: { company },
  } = useCompanyContext();

  const { isIdle, mutate } = useMutation({
    mutationFn: async () => {
      const site = await getCurrentSite();
      if (!site?.name) {
        devLog({ level: "warn", message: "Pathfinder returned no site name, leaving it unset" });
        return null;
      }

      return editCompany({ siteName: site.name });
    },
    onError: (error: Error) => {
      devLog({ extra: { error: error.message }, level: "warn", message: "Could not persist the site name" });
    },
    onSuccess: (updated) => {
      if (updated) setCompany(updated);
    },
  });

  useEffect(() => {
    // isIdle, not isPending: permission to run comes from not having attempted yet. Pathfinder can
    // legitimately return no name, so "has a siteName" never becomes true and only isIdle terminates
    if (!compiledClientConfig.spog || !company || company.siteName || !user?.isAdmin || !isIdle) return;

    mutate();
  }, [company, isIdle, mutate, user?.isAdmin]);
};
